import { useEffect, useMemo, useState } from 'react'
import { fetchTokenPrices } from '../util/dexscreener'
import { tokenList } from '../constants/tokens'

const FIXED_FEE_RATE = 0.003

const CHAIN_NAME_TO_ID = {
  ethereum: 1,
  bsc: 56,
  arbitrum: 42161,
  base: 8453,
  pulsechain: 369,
}

const STABLE_SYMBOLS = new Set([
  'USDC',
  'USDT',
  'DAI',
  'USDBC',
  'FDUSD',
  'TUSD',
  'USDE',
  'USDS',
  'USD1',
])

function toNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function normalizeSymbol(symbol) {
  return String(symbol || '').replace(/[^a-z0-9]/gi, '').toUpperCase()
}

function decimalsForSymbol(symbol) {
  return STABLE_SYMBOLS.has(normalizeSymbol(symbol)) ? 2 : 6
}

function formatAmount(value, maxFraction = 6) {
  const num = toNumber(value)
  if (!num) return '0'
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFraction,
  })
}

function parseAmount(value) {
  if (value == null) return 0
  const cleaned = String(value).replace(/,/g, '').trim()
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : 0
}

function resolveChainId(token) {
  if (token?.chainId) return Number(token.chainId)
  if (!token?.chain) return 0
  return CHAIN_NAME_TO_ID[String(token.chain).toLowerCase()] || 0
}

function resolveAddress(token, chainId) {
  if (token?.address) return token.address
  if (!token?.symbol || !chainId) return ''
  const symbol = String(token.symbol).toUpperCase()
  const found = tokenList.find(
    (t) => Number(t.chainId) === Number(chainId) && String(t.symbol || '').toUpperCase() === symbol
  )
  return found?.address || ''
}

function resolveTokenForQuote(token) {
  if (!token) return null
  const chainId = resolveChainId(token)
  const address = resolveAddress(token, chainId)
  if (!chainId || !address) return null
  return {
    symbol: token.symbol || '',
    chainId,
    address,
  }
}

export function useSwapQuote({ payToken, receiveToken, payAmount }) {
  const [state, setState] = useState({
    receiveAmount: '0',
    rateText: '1 : 1',
    feeText: '0',
    loading: false,
  })

  const pay = useMemo(
    () => resolveTokenForQuote(payToken),
    [payToken?.symbol, payToken?.chain, payToken?.chainId, payToken?.address]
  )
  const receive = useMemo(
    () => resolveTokenForQuote(receiveToken),
    [receiveToken?.symbol, receiveToken?.chain, receiveToken?.chainId, receiveToken?.address]
  )
  const amountIn = useMemo(() => parseAmount(payAmount), [payAmount])
  const payChainId = pay?.chainId || 0
  const payAddress = pay?.address || ''
  const receiveChainId = receive?.chainId || 0
  const receiveAddress = receive?.address || ''
  const paySymbol = pay?.symbol || payToken?.symbol || ''
  const receiveSymbol = receive?.symbol || receiveToken?.symbol || ''

  useEffect(() => {
    let cancelled = false

    async function quote() {
      if (!payChainId || !payAddress || !receiveChainId || !receiveAddress || amountIn <= 0) {
        setState({
          receiveAmount: '0',
          rateText: `1 ${paySymbol} : 0 ${receiveSymbol}`.trim(),
          feeText: `0 ${paySymbol} (0.3%)`.trim(),
          loading: false,
        })
        return
      }

      setState((prev) => ({ ...prev, loading: true }))
      const quotePromise = fetchTokenPrices([
        { chainId: payChainId, address: payAddress },
        { chainId: receiveChainId, address: receiveAddress },
      ])
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Quote timeout')), 8000)
      )
      const { priceMap } = await Promise.race([quotePromise, timeoutPromise])
      if (cancelled) return

      const payKey = `${payChainId}-${String(payAddress).toLowerCase()}`
      const receiveKey = `${receiveChainId}-${String(receiveAddress).toLowerCase()}`
      const payUsd = toNumber(priceMap?.[payKey])
      const receiveUsd = toNumber(priceMap?.[receiveKey])

      if (!payUsd || !receiveUsd) {
        setState({
          receiveAmount: '0',
          rateText: `1 ${pay.symbol} : 0 ${receive.symbol}`,
          feeText: `0 ${receive.symbol} (0.3%)`,
          loading: false,
        })
        return
      }

      const grossOut = amountIn * (payUsd / receiveUsd)
      const feeInPay = amountIn * FIXED_FEE_RATE
      const netOut = Math.max(grossOut * (1 - FIXED_FEE_RATE), 0)
      const netRate = (payUsd / receiveUsd) * (1 - FIXED_FEE_RATE)

      setState({
        receiveAmount: formatAmount(netOut, decimalsForSymbol(receiveSymbol)),
        rateText: `1 ${paySymbol} : ${formatAmount(netRate, decimalsForSymbol(receiveSymbol))} ${receiveSymbol}`,
        feeText: `${formatAmount(feeInPay, decimalsForSymbol(paySymbol))} ${paySymbol} (0.3%)`,
        loading: false,
      })
    }

    quote().catch(() => {
      if (cancelled) return
      setState({
        receiveAmount: '0',
        rateText: `1 ${paySymbol} : 0 ${receiveSymbol}`.trim(),
        feeText: `0 ${paySymbol} (0.3%)`.trim(),
        loading: false,
      })
    })

    return () => {
      cancelled = true
    }
  }, [payChainId, payAddress, receiveChainId, receiveAddress, amountIn, paySymbol, receiveSymbol])

  return state
}

